<template>
  <AttrGroup
    title="绑定数据"
    :show-add="showAddBtn"
    card
    :visible="!!shape.attrs.sourceEnabled"
    :class="{
      'border-[2px] rounded-md': showGuideBorder && !shape.attrs.source
    }"
    @add="() => toggleSourceEnabled(true)"
  >
    <!-- 当前已选数据源 -->
    <div class="h-13 px-5 flex items-center justify-between w-full">
      <div class="cursor-pointer flex items-center" @click="openSourceModal">
        <span
          class="font-medium text-sm opacity-[0.3]"
          :class="{
            'attr-disabled': attrDisabled,
            'text-base underline opacity-[1.0]': dataSourceItem
          }"
        >
          {{ dataSourceItem ? dataSourceItem.label : '请选择...' }}
        </span>
        <span v-if="isShowDefaultValueTooltip" class="ml-1 mb-1">
          <a-tooltip :title="'可到配置区域，进行年份默认值的选择'">
            <icon icon="help.svg" :width="14" :height="14" fill="none" />
          </a-tooltip>
        </span>
      </div>
      <AttrDeleteIcon
        class="opacity-70"
        :class="{
          'attr-disabled': attrDisabled
        }"
        @delete="() => toggleSourceEnabled(false)"
      />
    </div>

    <!-- 数据源返回值 -->
    <DataSourceReturn :source-item="dataSourceItem" />

    <!-- 温度单位 -->
    <template
      v-if="
        supportTemType &&
        // M66时去掉11
        watchfaceStore.deviceType != 'M66'
      "
    >
      <DataSourceTemperatureType :shape="shape" :disabled="!!attrDisabled" />
    </template>

    <!-- 
      判断是否是1.5版本温度类数据源，如果是，提示是否可跟随系统切换即可，主要目的是让设计师明确
    -->
    <template v-if="needChoseRenderRule">
      <DataSourceTemperatureRenderRule
        :shape="shape"
        :disabled="!!attrDisabled"
      />
    </template>

    <!-- 最小值和最大值 -->
    <template v-if="showValueConfig">
      <DataSourceValue :shape="shape" :disabled="!!attrDisabled" />
    </template>

    <!-- 旋转角度 -->
    <template v-if="showAngleConfig">
      <div class="py-2.5">
        <div class="border-t border-white/10"></div>
      </div>
      <!-- 旋转属性 -->
      <div class="mt-2.5">
        <DataSourceAngle
          :shape="shape"
          :disabled="!!attrDisabled || shape.type == ShapeType.ProgressBar"
        />
      </div>
      <!-- 旋转中心：统一都加上旋转中心 -->
      <div
        v-if="[ShapeType.Image, ShapeType.ProgressBar].includes(shape.type)"
        class="mt-2.5"
      >
        <DataSourcePivot :shape="shape" :disabled="!!attrDisabled" />
      </div>
    </template>

    <!-- 超出范围是否显示，为兼容1.5 -->
    <div v-if="shape.type == ShapeType.Image" class="mt-2.5">
      <DataSourceRenderRule :shape="shape" :disabled="!!attrDisabled" />
    </div>

    <!-- 图组 -->
    <template v-if="props.shape.type == ShapeType.ImageArray">
      <DataSourceImageArrayType :shape="shape" :disabled="!!attrDisabled" />
      <DataSourceImageArrayList :shape="shape" :disabled="!!attrDisabled" />
      <!-- 24或12小时制 -->
      <DataSourceHourMode
        v-if="
          shape.attrs.source === 'timeHour' &&
          canUseDataSource('timeHour24H', watchfaceStore.deviceType as 'N62')
        "
        :shape="shape"
        :disabled="!!attrDisabled"
      />
    </template>

    <!-- 数据源格式 -->
    <template v-if="showFormat">
      <DataSourceFormatUnits :shape="shape" :disabled="!!attrDisabled" />
    </template>

    <!-- 图组拼接数据时的，图片上传 -->
    <template v-if="shape.widgetImageArray">
      <DataSourceImageArrayWidgetUnits
        :shape="shape"
        :disabled="!!attrDisabled"
      />
    </template>

    <!-- 指南针旋转 -->
    <template
      v-if="
        (props.shape.type == ShapeType.ImageArray ||
          props.shape.type == ShapeType.Image) &&
        shape.attrs.source === 'systemSensorCompass' &&
        supportCompassRotation
      "
    >
      <AttrSelect
        label="指南针旋转"
        :value="shape.attrs.compassFps ?? 50"
        :options="[
          { value: 50, label: '50帧' },
          { value: 100, label: '100帧' }
        ]"
        :width="170"
        :disabled="!!attrDisabled"
        @change="(val:any)=> shape.setAttrs({
          compassFps:val
        })"
      />
    </template>

    <template
      v-if="
        shape.type == ShapeType.ImageArray &&
        shape.attrs.fillType == '数字填充' &&
        includePrecision
      "
    >
      <point-position :shape="shape" :disabled="!!attrDisabled" />
      <TrailingZero :shape="shape" :disabled="!!attrDisabled" />
    </template>

    <!-- 图组：对齐 -->
    <template
      v-if="
        props.shape.type == ShapeType.ImageArray &&
        shape.attrs.fillType === '数字填充'
      "
    >
      <DataSourceImageAlign :shape="shape" :disabled="!!attrDisabled" />
    </template>
  </AttrGroup>

  <modal-data-source
    ref="sourceModal"
    :shape-types="allowShapeTypes"
    :source-key="dataSourceItem?.key"
    :source-tags="
      [ShapeType.Text, ShapeType.Image].includes(shape.type)
        ? ['normal', 'subunit_1']
        : ['normal', 'compound']
    "
    @select="onSelectSource"
    @closed="onSourceModalClosed"
  />

  <!-- 图组新手引导 -->
  <modal-image-array-guide
    ref="guideModal"
    @on-ok="() => toggleSourceEnabled(true)"
  />
</template>

<script lang="ts" setup>
import { useDataSource } from '@/hooks';
import { Shape, ShapeType } from '@/services/shape';
import { Attributes } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { supportTemperatureType } from '@/utils/ast/utils/image-array.utils';
import {
  canUseDataSource,
  canUseDataSourceByShape
} from '@/utils/can-use.utils';
import { EventType, emitter } from '@/utils/emitter.utils';

import { PropType, computed, nextTick, onMounted, ref, watch } from 'vue';
import { canUse } from '@/utils/can-use.utils';

const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const { findOne, getValueStartEnd, getFormatOptions } = useDataSource();

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const showAddBtn = computed(() => {
  const { attrs } = props.shape;
  return !attrs.sourceEnabled;
});

// 用户所选的数据源：对应modal中的数据源
const dataSourceItem = computed(() => findOne(props.shape.attrs.source ?? ''));
const includePrecision = computed(() =>
  props.shape.sourceItems.some((item) => !!item?.precision)
);

const showDefaultValueSources = [
  'dateYear',
  'dateLunarYear',
  'dateLunarStringYear',
  'dateSexagenaryCycle'
];
// 是否出现提醒设置默认数据源的提示
const isShowDefaultValueTooltip = computed(() => {
  const canDatasourceChangeDefault =
    showDefaultValueSources.indexOf(dataSourceItem.value?.key ?? '') > -1;
  return canDatasourceChangeDefault && canUse('set-default-data-source').valid;
});
// 是否支持指南针旋转 图片和图组
const supportCompassRotation = computed(() => canUse('compass-rotation').valid);

const _setAttrs = (data: Partial<Attributes>) => {
  const _data = { ...data };
  // 如果设置数据源
  const keys = Object.keys(_data);
  if (keys.includes('source')) {
    const { valueStart, valueEnd } = getValueStartEnd(data.source ?? '') ?? {};
    _data.valueStart = valueStart;
    _data.valueEnd = valueEnd;

    // 设置图片旋转角度
    if (props.shape.type == ShapeType.Image) {
      const sourceKey = data.source ?? '';
      if (
        [
          'time',
          'timeHour',
          'dualtime',
          'dualtimeHour',
          'timeHour24H',
          'timeHour12H'
        ].includes(sourceKey)
      ) {
        _data.angleStart = 0;
        _data.angleEnd = 720;
      } else if (
        [
          'timeMinute',
          'timeSecond',
          'dualtimeMinute',
          'dualtimeSecond'
        ].includes(sourceKey)
      ) {
        _data.angleStart = 0;
        _data.angleEnd = 360;
      } else {
        _data.angleStart = -90;
        _data.angleEnd = 90;
      }
    }
  }
  props.shape.setAttrs(_data);
};

// 启用或禁用数据源
const toggleSourceEnabled = (enabled: boolean) => {
  if (attrDisabled?.value) return;
  if (enabled) {
    // 设置数据源
    _setAttrs({
      sourceEnabled: !!props.shape.attrs.source,
      sourceFormat: undefined
    });
    // 自动打开modal
    if (!props.shape.attrs.source) {
      openSourceModal();
    }
  } else {
    // 禁用数据源
    _setAttrs({
      sourceEnabled: false,
      source: '',
      valueEnd: '',
      valueStart: '',
      sourceFormat: undefined,
      sourceProgressAccuracy: 10,
      sourceWeatherType: 'whole',
      fillType: undefined
    });
  }
};

//添加图组时，默认打开数据源
const guideModal = ref();
const showGuideBorder = ref(false);
const autoOpenIfNeed = () => {
  showGuideBorder.value = false;
  const shape = props.shape;
  if (shape.type != ShapeType.ImageArray) return;
  if (shape.attrs.source) return;
  // 如果没有数据源
  // 显示新手引导（如果没有显示过）
  if (editorStore.autoOpenDataSourceShapeId != shape.id) return;
  if (!editorStore.imageArrayGuildeModleShown) {
    editorStore.imageArrayGuildeModleShown = true;
    editorStore.autoOpenDataSourceShapeId = '';
    guideModal.value?.open?.();
    showGuideBorder.value = true;
  } else {
    if (editorStore.autoOpenDataSourceShapeId == shape.id) {
      toggleSourceEnabled(true);
      editorStore.autoOpenDataSourceShapeId = '';
    }
  }
};

watch(() => props.shape.id, autoOpenIfNeed);
onMounted(async () => {
  await nextTick();
  autoOpenIfNeed();
});

// 数据源modal
const sourceModal = ref();
const allowShapeTypes = computed(() => {
  const { type } = props.shape;
  if (type == ShapeType.Image) {
    return ['image'];
  }
  if (type == ShapeType.Text) {
    return ['text'];
  }
  if (type == ShapeType.ImageArray) {
    return ['image-array-digit', 'image-array-normal'];
  }
  if (type == ShapeType.ProgressBar) {
    return ['progress-bar'];
  }
  return [];
});
const openSourceModal = async () => {
  if (attrDisabled?.value) return;
  await nextTick();
  sourceModal.value?.open?.();
};
const onSelectSource = (item: DataSourceItem) => {
  const { type } = props.shape;
  const data: Partial<Attributes> = {
    source: item.key,
    sourceEnabled: true
  };
  const options = getFormatOptions(data.source ?? '', props.shape.type);
  data.sourceFormat = options?.length ? options?.[0].value : undefined;

  if (type == ShapeType.ImageArray) {
    // 选择数据源后，根据数据源支持的填充类型，设置图组的填充类型
    const allowNormalType = canUseDataSourceByShape({
      sourceKey: item.key,
      deviceType: watchfaceStore.deviceType,
      shapeType: 'image-array-normal',
      themeType: editorStore.theme?.type ?? 'normal'
    });
    const allowDigitType = canUseDataSourceByShape({
      sourceKey: item.key,
      deviceType: watchfaceStore.deviceType,
      shapeType: 'image-array-digit',
      themeType: editorStore.theme?.type ?? 'normal'
    });

    // 优先为普通填充，如果不能使用，则选择数字填充
    data['fillType'] = allowNormalType
      ? '普通填充'
      : allowDigitType
      ? '数字填充'
      : undefined;

    // 如果是进度需要重置默认值
    if (item.progressAccuracy?.length)
      data['sourceProgressAccuracy'] = item.progressAccuracy[0] ?? 10;
    if (item.weatherValueRange) data['sourceWeatherType'] = 'whole';

    // 切换到 widgetImageArray 类型数据源时，重置 align 为默认值
    if (item.widgetImageArray || data['fillType'] === '普通填充') {
      data['align'] = 'left';
    }
  }
  _setAttrs(data);
  emitter.emit(EventType['data-source:selected'], { sourceKey: item.key });
};
const onSourceModalClosed = () => {
  const shape = props.shape;
  if (!shape.attrs.source) shape.setAttrs({ sourceEnabled: false });
};

// 最小值和最大值
const showValueConfig = computed(() =>
  // 指针和进度条显示最小和最大值
  [ShapeType.Image, ShapeType.ProgressBar].includes(props.shape.type)
);

const showAngleConfig = computed(() => {
  // 指针和进度条显示最小和最大值
  const { type, attrs } = props.shape;
  return (
    type == ShapeType.Image ||
    (type == ShapeType.ProgressBar && attrs.progressBarType == 'arc')
  );
});
const showFormat = computed(() => {
  const { type, attrs } = props.shape;
  // 文本和数字填充的图组会有数据源格式
  return (
    type == ShapeType.Text ||
    (type == ShapeType.ImageArray && attrs.fillType == '数字填充')
  );
});

const supportTemType = computed(() =>
  supportTemperatureType(props.shape.type, props.shape.sourceItem?.key ?? '')
);

// 是否是图组非规范温度数据源
const needChoseRenderRule = computed(() => {
  const { type, attrs } = props.shape;
  const { source = '' } = attrs ?? {};
  return (
    type == ShapeType.ImageArray &&
    attrs.fillType == '数字填充' &&
    source.includes('Temperature') &&
    source != 'weatherCurrentTemperatureFeel' &&
    !source.endsWith('_temp')
  );
});
</script>
